import React, { memo, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { getConditionalArray } from 'utils/data';

import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';

import { getConfig, transactionDetailsConfigFields } from './TransactionDetails.config';
import {
  closingTypeVisibility,
  constructionOrImprovementCostsVisibility,
} from './TransactionDetails.constants';

const TransactionDetails = ({
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  applicationData,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  setForm,
}) => {
  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? applicationData : form;

  const isConstructionOrImprovementCostsVisible = constructionOrImprovementCostsVisibility.includes(
    entityData?.transactionDetail?.value,
  );
  const isClosingTypeVisible = closingTypeVisibility.includes(entityData?.transactionDetail?.value);
  const isEnergyImprovementAmountActive = entityData?.energyRelatedImprovementsIndicator;

  const config = useMemo(() => {
    return getConfig({
      isConstructionOrImprovementCostsVisible,
      isClosingTypeVisible,
      isEnergyImprovementAmountActive,
    });
  }, [
    isConstructionOrImprovementCostsVisible,
    isClosingTypeVisible,
    isEnergyImprovementAmountActive,
  ]);

  useEffect(() => {
    setEntityConfig(config);
  }, [config, setEntityConfig]);

  useEffect(() => {
    setForm(prev =>
      _.omit(prev, [
        ...getConditionalArray(!isClosingTypeVisible, [
          transactionDetailsConfigFields.transactionDetailClosingType.nameInModel,
        ]),
        ...getConditionalArray(!isConstructionOrImprovementCostsVisible, [
          transactionDetailsConfigFields.constructionOrImprovementCosts.nameInModel,
        ]),
      ]),
    );
  }, [setForm, isClosingTypeVisible, isConstructionOrImprovementCostsVisible]);

  useEffect(() => {
    if (isFormDataEmpty) {
      return;
    }

    if (form.energyRelatedImprovementsIndicator === false) {
      setForm(prev => ({
        ...prev,
        energyImprovementAmount: '',
        amountOfEnergyEfficiencyMortgage: '',
        listOfEnergyImprovements: '',
      }));
    }
  }, [form.energyRelatedImprovementsIndicator, isFormDataEmpty, setForm]);

  return (
    <Box className={className}>
      <Box sx={boxStyle}>
        <FormGenerator
          isNestedForm
          config={config}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.TRANSACTION_DETAILS}
          handleChange={handleChange}
          isDisabled={isLoading}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>
    </Box>
  );
};

TransactionDetails.propTypes = pagesPropTypes;

export default memo(TransactionDetails);
